import React, {useState, useCallback} from 'react'

import {updateUserMetadata} from '@daedalus/core/src/user/services'
import availableLanguages from '@daedalus/core/src/localization/services/locale'
import {splitPopularAndOtherCurrencies} from '@daedalus/core/src/localization/business/currency'

import {PreferencesLink} from './PreferencesLink'
import {PreferencesFormValues, PreferencesPopup} from './PreferencesPopup'

import {CurrencyType} from '@daedalus/core/src/localization/types/CurrencyType'

export enum SelectedElement {
  language = 'language',
  currency = 'currency'
}

interface ChangedPreferences {
  hasCurrencyChanged: boolean
  hasLanguageChanged: boolean
  currency: string
  language: string
}

interface Props {
  /** Currently selected currency */
  currentCurrency: string
  /** Currently selected language */
  currentLanguage: string
  /** List of currently enabled currencies */
  enabledCurrencies: CurrencyType[]
  /** Should it render the currency */
  hasCurrency?: boolean
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Callback action that should be called when preferences change */
  onPreferencesChange: (changedPreferences: ChangedPreferences) => void
  /** Callback action that should be called when preferences link is clicked */
  onPreferencesLinkClick: () => void
  /** Callback action that should be called when preferences select input is clicked */
  onSelectClick: (selectedElement: SelectedElement) => void
}

/**
 * Desktop header preferences widget which encapsulates the link and the popup
 * Introduced as part of experiment dq82hd-desktop-map
 */
export const PreferencesWidget = ({
  currentCurrency,
  currentLanguage,
  enabledCurrencies,
  hasCurrency,
  isAuthenticated,
  onPreferencesChange,
  onPreferencesLinkClick,
  onSelectClick
}: Props) => {
  const {
    currenciesList,
    popularCurrenciesList
  } = splitPopularAndOtherCurrencies(enabledCurrencies)

  const [IsPreferencePopupOpen, setIsPreferencePopupFormOpen] = useState(false)

  const handlePreferencesChange = useCallback(
    async ({currency, language}: PreferencesFormValues) => {
      const hasCurrencyChanged = currency !== currentCurrency
      const hasLanguageChanged = language !== currentLanguage

      if (hasCurrencyChanged || hasLanguageChanged) {
        if (hasLanguageChanged && isAuthenticated) {
          await updateUserMetadata({language})
        }

        onPreferencesChange({
          hasCurrencyChanged,
          hasLanguageChanged,
          currency,
          language
        })
      } else {
        setIsPreferencePopupFormOpen(false)
      }
    },
    [currentCurrency, currentLanguage, isAuthenticated, onPreferencesChange]
  )

  const handlePreferencesLinkClick = useCallback(() => {
    setIsPreferencePopupFormOpen(true)
    onPreferencesLinkClick()
  }, [onPreferencesLinkClick])

  return (
    <>
      <PreferencesLink
        currency={currentCurrency}
        language={currentLanguage}
        hasCurrency={hasCurrency}
        onClick={handlePreferencesLinkClick}
      />
      <PreferencesPopup
        isOpen={IsPreferencePopupOpen}
        currentCurrency={currentCurrency}
        currenciesList={currenciesList}
        popularCurrenciesList={popularCurrenciesList}
        currentLanguage={currentLanguage}
        availableLanguages={availableLanguages}
        hasCurrency={hasCurrency}
        onClose={() => setIsPreferencePopupFormOpen(false)}
        onChangePreferences={handlePreferencesChange}
        onClickLanguage={() => onSelectClick(SelectedElement.language)}
        onClickCurrency={() => onSelectClick(SelectedElement.currency)}
      />
    </>
  )
}
